import React,{ forwardRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Grid, Typography, IconButton, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { BaseBlockProps, ChartDataItem } from '../types';
import {
    BlockWrapper,
    BlockHeader,
    BlockToolbar,
    LoadingState,
} from '../base';
import { DonutChart } from '../../../Components/Display/charts';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface PercentageMatchingBlockProps extends BaseBlockProps {}

interface PieChartConfig {
    id: number;
    percentage: number;
    label: string;
    data: ChartDataItem[];
}

export const PercentageMatchingBlock = forwardRef<HTMLDivElement, PercentageMatchingBlockProps>(
    (
        {
            kedo,
            block,
            dashboard,
            onClose,
            onMax,
            onSuccess,
            getBlockHeader,
            dossier,
            link,
        },
        ref
    ) => {
        const [loading, setLoading] = useState(false);
        const [pieCharts, setPieCharts] = useState<PieChartConfig[]>([]);

        const fetchData = useCallback(async () => {
            const environment = kedo.env().getEnvironment();
            if (!environment || !block.id) return;

            const api = kedo.api();
            const params: Record<string, unknown> = {};

            if (dossier?.id) {
                params.dossier = dossier.id;
                params.linkId = link;
            }

            setLoading(true);

            try {
                const response = await api.get(
                    `${api.getMyPageBlockEndpoint(block.id)}/data`,
                    { params }
                );

                const data = response.data;
                if (data && Array.isArray(data.pie_charts)) {
                    const charts = data.pie_charts.map(
                        (chart: { id: number; matched: number; total: number; label: string }) => {
                            const percentage =
                                chart.total > 0
                                    ? Math.round((chart.matched / chart.total) * 100)
                                    : 0;

                            return {
                                id: chart.id,
                                percentage,
                                label: chart.label,
                                data: [
                                    {
                                        id: 'matched',
                                        label: kedo.t('Matched'),
                                        value: chart.matched,
                                    },
                                    {
                                        id: 'unmatched',
                                        label: kedo.t('Unmatched'),
                                        value: chart.total - chart.matched,
                                    },
                                ],
                            };
                        }
                    );
                    setPieCharts(charts);
                }
            } catch (error) {
                console.error('Failed to fetch percentage matching data:', error);
            } finally {
                setLoading(false);
            }
        }, [kedo, block, dossier, link]);

        useEffect(() => {
            fetchData();
        }, [fetchData]);

        const scheme = block.settings?.scheme ?? 'nivo';

        return (
            <BlockWrapper
                ref={ref}
                dashboard={dashboard}
                sx={{
                    height: '100%',
                    marginBottom: '30px',
                    minHeight: '300px',
                    minWidth: '300px',
                }}
            >
                <BlockHeader>
                    <BlockToolbar
                        kedo={kedo}
                        block={block}
                        onClose={onClose}
                        onMax={onMax}
                        onSuccess={onSuccess}
                        fetchData={fetchData}
                    />
                    {getBlockHeader?.(block, dossier)}
                </BlockHeader>
                {loading ? (
                    <LoadingState />
                ) : (
                    <Grid container spacing={2} sx={{ p: 2 }}>
                        {pieCharts.map((chart) => (
                            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={chart.id}>
                                <Box sx={{ height: 200, position: 'relative' }}>
                                    <DonutChart
                                        data={chart.data}
                                        percentage={chart.percentage}
                                        label={chart.label}
                                        colors={{ scheme }}
                                    />
                                    {kedo.isAdminOrEnvironmentAdmin() && (
                                        <Tooltip title={kedo.t('Edit')}>
                                            <IconButton
                                                size="small"
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEdit} size="sm" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Typography
                                    variant="subtitle2"
                                    align="center"
                                    color="text.secondary"
                                >
                                    {chart.label}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </BlockWrapper>
        );
    }
);

PercentageMatchingBlock.displayName = 'PercentageMatchingBlock';
